/**
 * deviceCheck — porta de `isMobileDevice`/`checkBrowser`/`alertBrowser` (system.js).
 * O painel exige um desktop com Chromium (Chrome/Edge). Antes do boot:
 *  - dispositivo móvel → tela "abra num computador" (unsupported_mobile);
 *  - navegador sem suporte (não Chrome/Edge) → tela "instale o Chrome"
 *    (unsupported_browser + botão → chrome).
 * Retorna `true` quando o app pode montar; `false` quando já renderizou o bloqueio.
 */
import { getBrand } from '../config/brand';

function isMobileDevice(): boolean {
  try {
    const uaData = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData;
    if (uaData && typeof uaData.mobile === 'boolean') return uaData.mobile;
    if (window.matchMedia?.('(pointer: coarse) and (max-width: 767px)').matches) return true;
    const ua = (navigator.userAgent || navigator.vendor || '').toLowerCase();
    return /android|iphone|ipad|ipod|ios/.test(ua);
  } catch {
    return false;
  }
}

/** Nome do navegador (legado checkBrowser) — só Chrome/Edge/Brave contam como Chromium. */
function checkBrowser(): string {
  try {
    const ua = navigator.userAgent;
    const w = window as Window & { opr?: { addons?: unknown }; opera?: unknown; chrome?: unknown };
    const isOpera = (!!w.opr && !!w.opr.addons) || !!w.opera || ua.indexOf(' OPR/') >= 0;
    const isEdge = ua.indexOf(' Edg/') >= 0;
    const isBrave = (navigator as Navigator & { brave?: unknown }).brave !== undefined;

    let name = 'other';
    if (/chrome|chromium|crios/i.test(ua)) name = 'chrome';
    else if (/firefox|fxios/i.test(ua)) name = 'firefox';
    else if (/safari/i.test(ua)) name = 'safari';
    else if (/opr\//i.test(ua)) name = 'opera';
    else if (/edg/i.test(ua)) name = 'edge';

    if (isOpera) name = 'opera';
    if (isEdge) name = 'edge';
    if (isBrave) name = 'brave';
    return name;
  } catch {
    return 'other';
  }
}

/** Renderiza um bloqueio de tela cheia (legado: div.dialog no body). */
function renderDialog(root: HTMLElement, heading: string, button?: { label: string; href: string }): void {
  const dialog = document.createElement('div');
  dialog.className = 'dialog';
  const h3 = document.createElement('h3');
  h3.textContent = heading;
  dialog.appendChild(h3);
  if (button) {
    const btn = document.createElement('button');
    btn.textContent = button.label;
    btn.onclick = () => {
      window.location.href = button.href;
    };
    dialog.appendChild(btn);
  }
  root.innerHTML = '';
  document.body.appendChild(dialog);
}

/**
 * Verifica dispositivo/navegador. Recebe o tradutor já inicializado (i18n) e o elemento
 * root. Retorna false (e renderiza o bloqueio) quando o app NÃO deve montar.
 */
export function deviceSupported(root: HTMLElement, t: (key: string, fallback: string) => string): boolean {
  if (isMobileDevice()) {
    renderDialog(root, t('unsupported_mobile', 'Acesse o painel por um computador para usar todos os recursos. 😊'));
    return false;
  }
  if (!['chrome', 'edge', 'brave'].includes(checkBrowser())) {
    const heading =
      getBrand().source === 'lead-machine'
        ? t('unsupported_browser_lead_machine', 'Seu navegador não é compatível. Use o Google Chrome.')
        : t('unsupported_browser_eminer', 'Seu navegador não é compatível. Use o Google Chrome.');
    renderDialog(root, heading, {
      label: t('install_chrome', 'Instalar o Chrome'),
      href: 'https://www.google.com/chrome',
    });
    return false;
  }
  return true;
}
